import React, { useCallback, useEffect, useMemo, useState } from "react";
import { dbmCall } from "../utils/dbmApi";

/**
 * H10SelfInitPanel — per-tenant H-10 setup view embedded in CompanyHome at
 * /companyHome/<subdomain>. Company-admin self-service (the server only mints
 * the h10:selfinit scope for tenant Role-'A' users).
 *
 * 2026-07-27 (Lori Stoker's Freshdesk notes):
 *   - Edit any lease that is already Configured (h10_configured_update —
 *     server re-validates the merged row and refuses to create new rows)
 *   - Sort + search across Lease ID / Lease Name / Well #, CSV export
 *   - Inactive leases no longer appear under Needs Setup (server-side filter;
 *     a count of hidden inactive leases is shown for transparency)
 *   - Exclude a lease from H-10 (e.g. 1182W.WSW water supply well) with a
 *     reason + an Excluded tab with Restore
 *
 * Backend: /api/db_manager.php through the shared dbmApi client, which owns
 * the token lifecycle (house session OR OgPumper dashboard-link exchange).
 * `h10_self_init_status` is called first; only a real server answer of
 * enabled=false renders the "not enabled" state.
 */

const FLUID_KEYS = ["SWPct", "NORMPct", "FreshPct", "TotalCO2Pct", "NGPct", "H2SPct", "PolymerPct", "SteamPct", "AirPct", "NitrogenPct", "FracWaterFBPct", "OtherPct"];
const FLUID_EXTRA = ["NORMPct", "FreshPct", "TotalCO2Pct", "AnthropogenicCO2Pct", "NGPct", "H2SPct", "PolymerPct", "SteamPct", "AirPct", "NitrogenPct", "FracWaterFBPct", "OtherPct"];

const MODAL_FIELDS = [
  { k: "WellNumber", label: "Well Number", kind: "text", help: "Well number on the lease." },
  { k: "H10MonthDue", label: "H-10 Due Month (MM)", kind: "text", ph: "09", help: "Annual H-10 due month, 01–12." },
  { k: "H10API", label: "API # (8 digits)", kind: "text", ph: "42xxxxxx", help: "8-digit Texas RRC API number." },
  { k: "UICType", label: "UIC Type", kind: "select", opts: [["", "— select —"], ["1", "1"], ["2", "2"], ["3", "3 — salt-water disposal"], ["4", "4"], ["6", "6"]], help: "UIC well class. 3 = salt-water disposal." },
  { k: "InjThrough", label: "Injection Through", kind: "select", opts: [["", "— select —"], ["1", "Tubing (1)"], ["2", "Casing (2)"]], help: "Whether injection is through tubing (1) or casing (2)." },
  { k: "InjIntervalDepthTop", label: "Interval Top (ft)", kind: "num", help: "Injection interval TOP depth." },
  { k: "InjIntervalDepthBottom", label: "Interval Bottom (ft)", kind: "num", help: "Injection interval BOTTOM depth (must exceed top)." },
  { k: "TbgPackerDepth", label: "Tubing/Packer Depth (ft)", kind: "num", help: "Tubing/packer setting depth." },
  { k: "SWPct", label: "Salt Water %", kind: "num", help: "Salt-water share. All fluid % must total 100." },
];

const EXCLUDE_REASONS = [
  "Water supply well — not an injection well",
  "Not an injection/disposal well",
  "Plugged / no longer in service",
  "Other",
];

function rowMissing(r) {
  const m = [];
  const s = (v) => (v == null ? "" : String(v)).trim();
  const pos = (v) => parseInt(s(v), 10) > 0;
  if (s(r.LeaseID) === "") m.push("LeaseID");
  if (s(r.WellNumber) === "") m.push("WellNumber");
  if (!/^(0[1-9]|1[0-2])$/.test(s(r.H10MonthDue))) m.push("H10MonthDue");
  if (!/^\d{8}$/.test(s(r.H10API))) m.push("H10API");
  if (!["1", "2", "3", "4", "6"].includes(s(r.UICType))) m.push("UICType");
  if (!pos(r.InjIntervalDepthTop)) m.push("InjIntervalDepthTop");
  if (!pos(r.InjIntervalDepthBottom)) m.push("InjIntervalDepthBottom");
  if (!pos(r.TbgPackerDepth)) m.push("TbgPackerDepth");
  if (!["1", "2"].includes(s(r.InjThrough))) m.push("InjThrough");
  const sum = FLUID_KEYS.reduce((a, k) => a + (parseFloat(r[k]) || 0), 0);
  if (Math.abs(sum - 100) > 0.01) m.push("FluidPctSum100");
  if ((parseFloat(r.OtherPct) || 0) > 0 && (r.OtherType1 == null || String(r.OtherType1).trim() === "")) m.push("OtherType");
  return m;
}
const rowReady = (r) => r.status !== "Finalized" && rowMissing(r).length === 0;
function effCat(r) {
  if (r.category === "already_configured") return "already_configured";
  if (r.category === "excluded") return "excluded";
  return rowReady(r) ? "ready" : "needs_fields";
}

function rowToSaveObj(r) {
  const o = { LeaseID: r.LeaseID, LeaseName: r.LeaseName };
  MODAL_FIELDS.forEach(({ k }) => { o[k] = r[k]; });
  ["NORMPct", "FreshPct", "TotalCO2Pct", "AnthropogenicCO2Pct", "NGPct", "H2SPct", "PolymerPct", "SteamPct", "AirPct", "NitrogenPct", "FracWaterFBPct", "OtherPct", "InjFluidSourceOthers"].forEach((f) => { if (r[f] != null) o[f] = r[f]; });
  return o;
}

/** CSV cell quoting (RFC-ish; Excel-safe with the BOM added at export). */
function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function H10SelfInitPanel({ subdomain, theme = "light", companyName }) {
  const isDark = theme === "dark";
  const [status, setStatus] = useState({ checking: true, ok: false, reason: null });
  const [rows, setRows] = useState(null);
  const [serverCounts, setServerCounts] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("needs_fields");
  const [editRow, setEditRow] = useState(null);
  const [editMode, setEditMode] = useState("setup"); // 'setup' | 'configured'
  const [excludeRow, setExcludeRow] = useState(null);
  const [excludeReason, setExcludeReason] = useState(EXCLUDE_REASONS[0]);
  const [excludeOther, setExcludeOther] = useState("");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("LeaseID");
  const [sortDir, setSortDir] = useState(1);

  const card = isDark ? "bg-gray-800 border-gray-700 text-gray-100" : "bg-white border-gray-200 text-gray-800";
  const sub = isDark ? "border-gray-700" : "border-gray-200";
  const btn = "px-3 py-1.5 rounded text-sm font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed";
  const btnP = isDark ? "bg-blue-600 border-blue-500 text-white hover:bg-blue-500" : "bg-blue-600 border-blue-600 text-white hover:bg-blue-700";
  const btnN = isDark ? "bg-gray-700 border-gray-600 text-gray-100 hover:bg-gray-600" : "bg-gray-100 border-gray-300 text-gray-800 hover:bg-gray-200";
  const inpCls = (bad) => `w-full px-2 py-1 text-sm rounded border ${bad ? "border-red-500 ring-1 ring-red-500" : isDark ? "border-gray-600" : "border-gray-300"} ${isDark ? "bg-gray-900 text-gray-100" : "bg-white"}`;

  const callApi = useCallback(
    (action, payload = {}) => dbmCall(action, { init_subdomain: subdomain, ...payload }),
    [subdomain]
  );

  const refresh = useCallback(async () => {
    setBusy("preview"); setError("");
    try {
      const resp = await callApi("h10_candidates_preview");
      const res = resp?.data?.result;
      setRows((res?.candidates || []).map((c) => ({ ...c })));
      setServerCounts(res?.counts || null);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(""); }
  }, [callApi]);

  // Status check first — confirms the tenant flag is ON; if not we refuse to render the form.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await callApi("h10_self_init_status");
        if (cancelled) return;
        const enabled = !!(resp?.data?.enabled);
        setStatus({ checking: false, ok: enabled, notEnabled: !enabled, reason: enabled ? null : (resp?.data?.reason || "Self-init not enabled for this tenant.") });
        if (enabled) refresh();
      } catch (e) {
        if (cancelled) return;
        if (e.code === "company_not_enabled") {
          setStatus({ checking: false, ok: false, notEnabled: true, reason: "H-10 self-setup is switched off for this company." });
        } else {
          setStatus({
            checking: false,
            ok: false,
            notEnabled: false,
            isAuth: !!e.isAuth,
            isNetwork: e.code === "network",
            reason: e.message || "Status check failed.",
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [callApi, refresh]);

  const counts = useMemo(() => {
    const c = { ready: 0, needs_fields: 0, already_configured: 0, excluded: 0 };
    (rows || []).forEach((r) => { c[effCat(r)] = (c[effCat(r)] || 0) + 1; });
    return c;
  }, [rows]);

  // Tab filter → search filter → sort. Search covers Lease ID, Lease Name and
  // Well # (Lori: "help locate by either LeaseID, Lease Name, Well #").
  const tabRows = useMemo(() => {
    const inTab = (rows || []).filter((r) => {
      const cat = effCat(r);
      if (tab === "already_configured") return cat === "already_configured";
      if (tab === "excluded") return cat === "excluded";
      return cat === "needs_fields" || cat === "ready";
    });
    const q = search.trim().toLowerCase();
    const found = q === ""
      ? inTab
      : inTab.filter((r) =>
          String(r.LeaseID || "").toLowerCase().includes(q) ||
          String(r.LeaseName || "").toLowerCase().includes(q) ||
          String(r.WellNumber || "").toLowerCase().includes(q));
    const dir = sortDir;
    const keyVal = (r) => {
      if (sortKey === "Status") return effCat(r);
      return String(r[sortKey] ?? "").toLowerCase();
    };
    return [...found].sort((a, b) => {
      const ka = keyVal(a); const kb = keyVal(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return String(a.LeaseID).localeCompare(String(b.LeaseID));
    });
  }, [rows, tab, search, sortKey, sortDir]);

  const clickSort = (k) => {
    if (sortKey === k) setSortDir((d) => -d);
    else { setSortKey(k); setSortDir(1); }
  };
  const sortMark = (k) => (sortKey === k ? (sortDir === 1 ? " ▲" : " ▼") : "");

  const exportCsv = () => {
    const header = ["LeaseID", "LeaseName", "WellNumber", "Status", "H10MonthDue", "H10API", "UICType", "MissingFields", "ExclusionReason"];
    const lines = [header.join(",")];
    tabRows.forEach((r) => {
      const cat = effCat(r);
      const statusLabel = cat === "ready" ? "Ready" : cat === "needs_fields" ? "Needs Setup" : cat === "excluded" ? "Excluded" : "Configured";
      lines.push([
        csvCell(r.LeaseID), csvCell(r.LeaseName), csvCell(r.WellNumber),
        csvCell(statusLabel), csvCell(r.H10MonthDue), csvCell(r.H10API), csvCell(r.UICType),
        csvCell(cat === "needs_fields" ? rowMissing(r).join(" ") : ""),
        csvCell(r.exclusion_reason || ""),
      ].join(","));
    });
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const day = new Date().toISOString().slice(0, 10);
    a.download = `h10-setup-${subdomain}-${tab}-${day}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const editMissing = editRow ? rowMissing(editRow) : [];
  const editFluidTotal = editRow ? FLUID_KEYS.reduce((a, k) => a + (parseFloat(editRow[k]) || 0), 0) : 0;
  const setEdit = (k, v) => setEditRow((p) => ({ ...p, [k]: v }));

  const openEditor = (r, mode) => {
    const seed = { ...r };
    FLUID_KEYS.concat(["AnthropogenicCO2Pct"]).forEach((k) => { if (seed[k] == null) seed[k] = (k === "SWPct" ? 100 : 0); });
    setEditMode(mode);
    setEditRow(seed);
    setError(""); setNotice("");
  };

  const doSave = useCallback(async () => {
    if (!editRow) return;
    setBusy("save"); setError("");
    try {
      await callApi("h10_candidates_save_draft", { candidates: [rowToSaveObj(editRow)] });
      setNotice(`Saved ${editRow.LeaseID} — you can come back to finish later.`);
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [editRow, callApi, refresh]);

  const doSubmit = useCallback(async () => {
    if (!editRow) return;
    const miss = rowMissing(editRow);
    if (miss.length) { setError(`Some required fields are missing: ${miss.join(", ")}.`); return; }
    if (!window.confirm(`Submit H-10 setup for ${editRow.LeaseID}? It writes a regulatory record and cannot overwrite a well that is already set up.`)) return;
    setBusy("submit"); setError("");
    try {
      // Save the draft first (assigns a candidate_id and runs server-side validation).
      await callApi("h10_candidates_save_draft", { candidates: [rowToSaveObj(editRow)] });
      // Re-preview to resolve the candidate_id for this lease.
      const prev = await callApi("h10_candidates_preview");
      const prevCands = prev?.data?.result?.candidates || [];
      const match = prevCands.find((c) => c.LeaseID === editRow.LeaseID && c.can_finalize && c.candidate_id);
      if (!match) {
        setError("This lease did not validate server-side. Please re-check the required fields.");
        return;
      }
      const fin = await callApi("h10_candidates_finalize", { candidate_ids: [match.candidate_id] });
      const res = fin?.data?.result;
      const executed = res?.executed ?? res?.finalized ?? 0;
      const skipped = res?.skipped ?? 0;
      if (executed > 0) {
        setNotice(`Submitted — H-10 setup for ${editRow.LeaseID} is now on file.`);
        setEditRow(null);
      } else if (skipped > 0) {
        setNotice("This lease was already configured by someone else; no changes made.");
        setEditRow(null);
      } else {
        setError((res?.errors || []).map((e) => e.reason).join("; ") || "Submit could not complete.");
      }
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [editRow, callApi, refresh]);

  // Save changes to an ALREADY-CONFIGURED well (h10_configured_update).
  const doUpdateConfigured = useCallback(async () => {
    if (!editRow) return;
    const miss = rowMissing(editRow);
    if (miss.length) { setError(`Some required fields are missing: ${miss.join(", ")}.`); return; }
    if (!window.confirm(`Save changes to the H-10 setup for ${editRow.LeaseID}? This updates the well's H-10 record.`)) return;
    setBusy("update"); setError("");
    try {
      const resp = await callApi("h10_configured_update", { values: rowToSaveObj(editRow) });
      const res = resp?.data?.result;
      if (res?.ok !== false) {
        setNotice(`Updated — H-10 setup for ${editRow.LeaseID} saved.`);
        setEditRow(null);
      } else {
        setError((res?.validation_errors || []).join(", ") || res?.error || "Update could not complete.");
      }
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [editRow, callApi, refresh]);

  const doExclude = useCallback(async () => {
    if (!excludeRow) return;
    const reason = excludeReason === "Other" ? (excludeOther.trim() || "Other") : excludeReason;
    const wasConfigured = excludeRow.category === "already_configured";
    setBusy("exclude"); setError("");
    try {
      await callApi("h10_candidates_set_exclusion", { lease_id: excludeRow.LeaseID, excluded: true, reason: reason.slice(0, 64) });
      setNotice(wasConfigured
        ? `${excludeRow.LeaseID} excluded — its H-10 setup was removed. All values are kept and it can be restored from the Excluded tab.`
        : `${excludeRow.LeaseID} excluded from H-10. You can restore it from the Excluded tab.`);
      setExcludeRow(null);
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [excludeRow, excludeReason, excludeOther, callApi, refresh]);

  const doRestore = useCallback(async (r) => {
    setBusy("restore"); setError("");
    try {
      const resp = await callApi("h10_candidates_set_exclusion", { lease_id: r.LeaseID, excluded: false, reason: "" });
      setNotice(resp?.data?.result?.restored_configured
        ? `${r.LeaseID} restored — its H-10 setup is back on file exactly as before.`
        : `${r.LeaseID} restored to the H-10 setup list.`);
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [callApi, refresh]);

  // ── render ──────────────────────────────────────────────────────────────
  if (status.checking) {
    return <div className={`rounded-lg border p-4 ${card}`}>Checking H-10 self-setup access…</div>;
  }
  if (!status.ok) {
    return (
      <div className={`rounded-lg border p-4 ${card}`}>
        <h2 className="text-xl font-semibold mb-1">H-10 Well Setup</h2>
        {status.notEnabled ? (
          <p className="text-sm opacity-70">H-10 self-setup is not enabled for this company.{status.reason ? ` (${status.reason})` : ""}</p>
        ) : (
          <>
            <p className="text-sm" style={{ color: "#c0392b" }}>
              {status.isAuth
                ? ""
                : status.isNetwork
                  ? "The H-10 setup service could not be reached. "
                  : "The H-10 setup service reported a problem. "}
              {status.reason || ""}
            </p>
            {status.isAuth ? null : (
              <p className="text-xs opacity-60 mt-1">This is a technical problem, not a permissions setting — H-10 may well be enabled for this company. Please try again or contact support.</p>
            )}
          </>
        )}
      </div>
    );
  }

  const inactiveHidden = serverCounts?.inactive_skipped || 0;

  return (
    <div className={`rounded-lg border p-4 ${card}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold m-0">H-10 Well Setup{companyName ? <span className="text-xs font-normal opacity-70 ml-2">— {companyName}</span> : null}</h2>
          <p className="text-sm opacity-70 m-0">Fill out injection well setup details for H-10 reporting.</p>
        </div>
        <div className="flex gap-2">
          <button className={`${btn} ${btnN}`} disabled={!rows || !tabRows.length} onClick={exportCsv}>Export CSV</button>
          <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={refresh}>{busy === "preview" ? "…" : "Refresh"}</button>
        </div>
      </div>

      {error ? <div className="text-sm rounded p-2 mb-2" style={{ background: "#c0392b22", color: "#c0392b" }}>{error}</div> : null}
      {notice ? <div className="text-sm rounded p-2 mb-2" style={{ background: "#1e8a4922", color: "#1e8449" }}>{notice}</div> : null}

      <div className="text-xs opacity-70 mb-2">
        {counts.needs_fields + counts.ready} lease(s) need setup · {counts.already_configured} already set up · {counts.excluded} excluded
        {inactiveHidden > 0 ? ` · ${inactiveHidden} inactive lease(s) not shown` : ""}
      </div>

      <div className="flex gap-1 mb-2 flex-wrap items-center">
        <button className={`px-2 py-1 text-xs rounded border ${tab === "needs_fields" ? "font-bold" : "opacity-70"} ${sub}`} style={tab === "needs_fields" ? { background: "#b9770e", color: "#fff", borderColor: "#b9770e" } : {}} onClick={() => setTab("needs_fields")}>Needs Setup ({counts.needs_fields + counts.ready})</button>
        <button className={`px-2 py-1 text-xs rounded border ${tab === "already_configured" ? "font-bold" : "opacity-70"} ${sub}`} style={tab === "already_configured" ? { background: "#566573", color: "#fff", borderColor: "#566573" } : {}} onClick={() => setTab("already_configured")}>Already Set Up ({counts.already_configured})</button>
        <button className={`px-2 py-1 text-xs rounded border ${tab === "excluded" ? "font-bold" : "opacity-70"} ${sub}`} style={tab === "excluded" ? { background: "#7d3c98", color: "#fff", borderColor: "#7d3c98" } : {}} onClick={() => setTab("excluded")}>Excluded ({counts.excluded})</button>
        <input
          className={`${inpCls(false)} !w-56 ml-auto`}
          placeholder="Search lease ID, name, well #…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {rows ? (
        tabRows.length ? (
          <div className="overflow-auto" style={{ maxHeight: 400 }}>
            <table className="w-full text-xs border-collapse">
              <thead><tr className={isDark ? "text-gray-300" : "text-gray-600"}>
                <th className={`text-left p-1 border-b cursor-pointer select-none ${sub}`} onClick={() => clickSort("LeaseID")}>Lease ID{sortMark("LeaseID")}</th>
                <th className={`text-left p-1 border-b cursor-pointer select-none ${sub}`} onClick={() => clickSort("LeaseName")}>Lease Name{sortMark("LeaseName")}</th>
                <th className={`text-left p-1 border-b cursor-pointer select-none ${sub}`} onClick={() => clickSort("WellNumber")}>Well #{sortMark("WellNumber")}</th>
                <th className={`text-left p-1 border-b cursor-pointer select-none ${sub}`} onClick={() => clickSort("Status")}>Status{sortMark("Status")}</th>
                <th className={`text-left p-1 border-b ${sub}`}>{tab === "already_configured" ? "Due" : tab === "excluded" ? "Reason" : "What's missing"}</th>
                <th className={`text-left p-1 border-b ${sub}`}>Action</th>
              </tr></thead>
              <tbody>
                {tabRows.map((r) => {
                  const cat = effCat(r);
                  const miss = rowMissing(r);
                  const statusLabel = cat === "ready" ? "Ready" : cat === "needs_fields" ? "Needs Setup" : cat === "excluded" ? "Excluded" : "Configured";
                  const statusBg = cat === "ready" ? "#1e8449" : cat === "needs_fields" ? "#b9770e" : cat === "excluded" ? "#7d3c98" : "#566573";
                  return (
                    <tr key={r.LeaseID}>
                      <td className="p-1 align-top whitespace-nowrap font-mono">{r.LeaseID}</td>
                      <td className="p-1 align-top">{r.LeaseName || "—"}</td>
                      <td className="p-1 align-top">{r.WellNumber || "—"}</td>
                      <td className="p-1 align-top"><span className="px-1 rounded text-white" style={{ background: statusBg }}>{statusLabel}</span></td>
                      <td className="p-1 align-top opacity-80">
                        {cat === "already_configured"
                          ? (r.H10MonthDue || "—")
                          : cat === "excluded"
                            ? (r.exclusion_reason || "Excluded") + (r.archived_from_configured ? " · was set up (restorable)" : "")
                            : (miss.length ? miss.join(", ") : <span style={{ color: "#1e8449" }}>none ✓</span>)}
                      </td>
                      <td className="p-1 align-top whitespace-nowrap">
                        {(cat === "needs_fields" || cat === "ready") ? (
                          <>
                            <button className={`${btn} ${btnP} !px-2 !py-0.5 !text-xs`} disabled={!!busy} onClick={() => openEditor(r, "setup")}>{cat === "ready" ? "Edit" : "Fill Out"}</button>
                            <button className={`${btn} ${btnN} !px-2 !py-0.5 !text-xs ml-1`} disabled={!!busy} title="Exclude this lease from H-10 (e.g. a water supply well)" onClick={() => { setExcludeRow(r); setExcludeReason(EXCLUDE_REASONS[0]); setExcludeOther(""); setError(""); setNotice(""); }}>Exclude</button>
                          </>
                        ) : cat === "excluded" ? (
                          <button className={`${btn} ${btnN} !px-2 !py-0.5 !text-xs`} disabled={!!busy} onClick={() => doRestore(r)}>{busy === "restore" ? "…" : "Restore"}</button>
                        ) : cat === "already_configured" && r.can_edit ? (
                          <>
                            <button className={`${btn} ${btnP} !px-2 !py-0.5 !text-xs`} disabled={!!busy} onClick={() => openEditor(r, "configured")}>Edit</button>
                            <button className={`${btn} ${btnN} !px-2 !py-0.5 !text-xs ml-1`} disabled={!!busy} title="Remove this well's H-10 setup (kept in Excluded, restorable)" onClick={() => { setExcludeRow(r); setExcludeReason(EXCLUDE_REASONS[0]); setExcludeOther(""); setError(""); setNotice(""); }}>Exclude</button>
                          </>
                        ) : (
                          <span className="opacity-50">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-xs opacity-70 p-2">
            {search.trim()
              ? "No leases match your search."
              : tab === "already_configured"
                ? "Nothing here yet."
                : tab === "excluded"
                  ? "No leases have been excluded from H-10."
                  : "Nothing to set up — you're done!"}
          </div>
        )
      ) : (
        <div className="text-xs opacity-70">Loading…</div>
      )}

      {excludeRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => !busy && setExcludeRow(null)}>
          <div className={`w-full max-w-md rounded-lg border p-4 ${card}`} onClick={(e) => e.stopPropagation()}>
            <h4 className="text-base font-semibold mb-1">Exclude from H-10</h4>
            {excludeRow.category === "already_configured" ? (
              <p className="text-xs mb-2 rounded p-2" style={{ background: "#b9770e22", color: "#b9770e" }}>
                <span className="font-mono">{excludeRow.LeaseID}</span> is currently <b>set up for H-10</b>. Excluding it removes its
                H-10 setup so it no longer counts as an H-10 well. All of its values are kept in the Excluded tab and can be restored.
              </p>
            ) : (
              <p className="text-xs opacity-80 mb-2">
                <span className="font-mono">{excludeRow.LeaseID}</span> {excludeRow.LeaseName ? `— ${excludeRow.LeaseName}` : ""} will no longer appear under Needs Setup.
                Nothing is deleted — you can restore it any time from the Excluded tab.
              </p>
            )}
            <label className="text-xs font-semibold">Reason</label>
            <select className={inpCls(false)} value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)}>
              {EXCLUDE_REASONS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            {excludeReason === "Other" ? (
              <input className={`${inpCls(false)} mt-2`} maxLength={64} placeholder="Short reason…" value={excludeOther} onChange={(e) => setExcludeOther(e.target.value)} />
            ) : null}
            <div className="flex items-center justify-end gap-2 mt-4">
              <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={() => setExcludeRow(null)}>Cancel</button>
              <button className={`${btn} ${btnP}`} disabled={!!busy} onClick={doExclude}>{busy === "exclude" ? "…" : "Exclude from H-10"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {editRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => !busy && setEditRow(null)}>
          <div className={`w-full max-w-2xl max-h-[90vh] overflow-auto rounded-lg border p-4 ${card}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-base font-semibold">{editMode === "configured" ? "Edit H-10 setup" : "Fill out H-10 setup"}</h4>
              <button className={`${btn} ${btnN} !py-0.5`} disabled={!!busy} onClick={() => setEditRow(null)}>Close</button>
            </div>
            <div className="text-xs mb-2">
              <span className="font-mono">{editRow.LeaseID}</span> <span className="opacity-60">{editRow.LeaseName}</span>
              <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-semibold text-white" style={{ background: editMissing.length ? "#b9770e" : "#1e8449" }}>
                {editMissing.length ? `${editMissing.length} field(s) missing` : "Ready ✓"}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {MODAL_FIELDS.map((f) => {
                const bad = editMissing.includes(f.k) || (f.k === "SWPct" && editMissing.includes("FluidPctSum100"));
                return (
                  <div key={f.k}>
                    <label className="text-xs font-semibold flex items-center gap-1">{f.label}{bad ? <span style={{ color: "#c0392b" }}>*</span> : null}</label>
                    {f.kind === "select" ? (
                      <select className={inpCls(bad)} value={editRow[f.k] ?? ""} onChange={(e) => setEdit(f.k, e.target.value)}>
                        {f.opts.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                      </select>
                    ) : (
                      <input className={inpCls(bad)} inputMode={f.kind === "num" ? "numeric" : undefined} placeholder={f.ph || ""} value={editRow[f.k] ?? ""} onChange={(e) => setEdit(f.k, e.target.value)} />
                    )}
                    <div className="text-[10px] opacity-60 mt-0.5">{f.help}</div>
                  </div>
                );
              })}
            </div>
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-semibold">
                Advanced fluid composition — total {editFluidTotal}%{" "}
                <span style={{ color: Math.abs(editFluidTotal - 100) < 0.01 ? "#1e8449" : "#c0392b" }}>{Math.abs(editFluidTotal - 100) < 0.01 ? "✓" : "(must total 100)"}</span>
              </summary>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {FLUID_EXTRA.map((k) => (
                  <div key={k}>
                    <label className="text-[10px] font-semibold">{k.replace("Pct", " %")}</label>
                    <input className={inpCls(false)} inputMode="numeric" value={editRow[k] ?? 0} onChange={(e) => setEdit(k, e.target.value)} />
                  </div>
                ))}
              </div>
            </details>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={() => setEditRow(null)}>Cancel</button>
              {editMode === "configured" ? (
                <button className={`${btn} ${btnP}`} disabled={!!busy || editMissing.length > 0} onClick={doUpdateConfigured}>{busy === "update" ? "…" : "Save Changes"}</button>
              ) : (
                <>
                  <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={doSave}>{busy === "save" ? "…" : "Save for later"}</button>
                  <button className={`${btn} ${btnP}`} disabled={!!busy || editMissing.length > 0} onClick={doSubmit}>{busy === "submit" ? "…" : "Submit H-10 setup"}</button>
                </>
              )}
            </div>
            <p className="text-[10px] opacity-60 mt-2">
              {editMode === "configured"
                ? "Save Changes updates this well's existing H-10 record. All values are re-checked before anything is written."
                : "Save keeps your draft. Submit writes the validated values into the H-10 record. You cannot overwrite a well that is already set up."}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
